import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, Download, CheckCircle2, XCircle, 
  AlertTriangle, Clock, FileText, Table2, RefreshCw, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'clinical', label: 'Clinical Metrics', icon: '🏥' },
  { value: 'safety', label: 'Safety & Incidents', icon: '🛡️' },
  { value: 'regulatory', label: 'Regulatory Compliance', icon: '📋' },
  { value: 'staff', label: 'Staff Health', icon: '👥' },
  { value: 'patient_experience', label: 'Patient Experience', icon: '❤️' },
  { value: 'program', label: 'Program Overview', icon: '📊' }
];

export default function BatchImport() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('clinical');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/import/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data);
      }
    } catch (error) {
      console.error('Failed to load import history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [token]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx'];
      const isValid = validTypes.some(type => file.name.toLowerCase().endsWith(type));
      
      if (!isValid) {
        toast.error('Invalid file type', { description: 'Please select a CSV or Excel file' });
        return;
      }
      
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/template/${category}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${category}_import_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Template downloaded');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/import/batch?category=${category}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setImportResult(result);
        toast.success('Import completed', {
          description: `${result.successful} of ${result.total_rows} rows imported`
        });
        fetchHistory();
      } else {
        toast.error('Import failed', { description: result.detail });
        setImportResult({ error: result.detail });
      }
    } catch (error) {
      toast.error('Upload failed', { description: error.message });
      setImportResult({ error: error.message });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="batch-import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Upload className="h-8 w-8 text-primary" />
            Batch Data Import
          </h1>
          <p className="text-muted-foreground mt-1">Import metrics from CSV or Excel files</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Section */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Upload Data
              </CardTitle>
              <CardDescription>
                Select a category and upload a CSV or Excel file with your metrics data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category Selection */}
              <div className="space-y-2">
                <Label>Data Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <span className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          {cat.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label>File Upload</Label>
                <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Upload className="h-8 w-8 text-primary" />
                      </div>
                      {selectedFile ? (
                        <div className="space-y-1">
                          <p className="font-medium">{selectedFile.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="font-medium">Click to upload or drag and drop</p>
                          <p className="text-sm text-muted-foreground">CSV or Excel files (max 5MB)</p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleUpload} 
                  disabled={!selectedFile || uploading}
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Import Data
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={downloadTemplate} data-testid="download-template">
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
                {selectedFile && (
                  <Button variant="ghost" onClick={resetForm}>
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Import Result */}
          {importResult && (
            <Card className={importResult.error ? 'border-destructive' : 'border-green-500'}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  {importResult.error ? (
                    <XCircle className="h-5 w-5 text-destructive" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )}
                  Import Result
                </CardTitle>
              </CardHeader>
              <CardContent>
                {importResult.error ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Import Failed</AlertTitle>
                    <AlertDescription>{importResult.error}</AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <p className="text-3xl font-bold">{importResult.total_rows}</p>
                        <p className="text-sm text-muted-foreground">Total Rows</p>
                      </div>
                      <div className="text-center p-4 bg-green-500/10 rounded-lg">
                        <p className="text-3xl font-bold text-green-500">{importResult.successful}</p>
                        <p className="text-sm text-muted-foreground">Successful</p>
                      </div>
                      <div className="text-center p-4 bg-red-500/10 rounded-lg">
                        <p className="text-3xl font-bold text-red-500">{importResult.failed}</p>
                        <p className="text-sm text-muted-foreground">Failed</p>
                      </div>
                    </div>
                    
                    {importResult.successful > 0 && (
                      <Progress 
                        value={(importResult.successful / importResult.total_rows) * 100} 
                        className="h-2"
                      />
                    )}

                    {importResult.errors?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium mb-2">Errors:</p>
                        <ScrollArea className="h-32 rounded border p-2">
                          {importResult.errors.map((err, idx) => (
                            <div key={idx} className="text-sm py-1 border-b last:border-0">
                              <span className="text-muted-foreground">Row {err.row}:</span>{' '}
                              <span className="text-destructive">{err.error}</span>
                            </div>
                          ))}
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Instructions & History */}
        <div className="space-y-6">
          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">1</Badge>
                <p>Select the data category you want to import</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">2</Badge>
                <p>Download the template to see the required columns</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">3</Badge>
                <p>Fill in your data following the template format</p>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="shrink-0">4</Badge>
                <p>Upload your CSV or Excel file</p>
              </div>
              
              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription className="text-xs">
                  Ensure period_date is in YYYY-MM-DD format. Invalid rows will be skipped.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Import History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Recent Imports
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                </div>
              ) : importHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No import history yet
                </p>
              ) : (
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {importHistory.map((item, idx) => (
                      <div key={idx} className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm truncate max-w-[150px]">
                            {item.filename}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="text-green-500">{item.successful_rows} ✓</span>
                          {item.failed_rows > 0 && (
                            <span className="text-red-500">{item.failed_rows} ✗</span>
                          )}
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          {new Date(item.imported_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
